import { useEffect, useMemo } from "react";

import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { FETCH_PERFORMANCE_TRACKING_SUMMARY } from "@gql/mmm-performance-tracking";
import { useNorthbeamLazyQuery } from "@hooks/http";
import {
  type FetchPerformanceTrackingSummary,
  type FetchPerformanceTrackingSummaryVariables,
} from "@north-beam/nb-client-side";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

export const usePerformanceTrackingSummary = () => {
  const { selectedCycle } = useSelectedCycle();
  const { adoptedClientSetting } = useClientSettings();
  const { adoptedBudgetMix } = useAdoptedBudgetMix();
  const { kpis: clientKpis } = useClientKpis();
  const { kpi: adoptedClientSettingKpi } =
    useClientSettingData(adoptedClientSetting);

  const {
    loadData,
    data: { data, loading },
  } = useNorthbeamLazyQuery<
    FetchPerformanceTrackingSummary,
    FetchPerformanceTrackingSummaryVariables
  >(FETCH_PERFORMANCE_TRACKING_SUMMARY);

  useEffect(() => {
    let kpiId = adoptedClientSettingKpi?.id;

    if (!kpiId) {
      kpiId = clientKpis.find(
        (e) => e.kpi_name === adoptedBudgetMix?.data.kpi_name,
      )?.id;
    }

    if (selectedCycle?.id && kpiId) {
      loadData({
        variables: {
          cycleId: selectedCycle.id,
          kpiId: kpiId,
        },
      });
    }
  }, [
    adoptedClientSettingKpi?.id,
    loadData,
    selectedCycle?.id,
    clientKpis,
    adoptedBudgetMix?.data.kpi_name,
  ]);

  const summary = useMemo(() => {
    const performanceTrackingData =
      data?.me?.mmm?.performanceTracking?.summary ?? null;

    return performanceTrackingData;
  }, [data]);

  return { loading: loading || !summary?.spend, summary };
};
